import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { InputDatePicker } from '@retail/components';
import { ContainedButton, Dialog, OutlinedButton } from '@shared/components';
import { dateRangeSchema, DateRangeSchema } from '../../DateSchema';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useProductsTFunction } from '@retail/products/i18n';
import { useCallback } from 'react';
import { AssortmentTagWithSupplierItemId } from './AssortmentTagTableContent';
import { TagDateRangeInfo } from './TagDateRangeInfo';
import { isBefore } from 'date-fns';
import { UpdateOrCreateSupplierItemTag } from '@retail/products/types';

interface Props {
  action: 'add' | 'edit';
  itemToAssortmentTag: AssortmentTagWithSupplierItemId;
  addSupplierItemTag: (supplierItemTag: UpdateOrCreateSupplierItemTag) => void;
  onClose: () => void;
}

export function AddOrEditItemToAssortmentTagDialog({
  action,
  itemToAssortmentTag,
  addSupplierItemTag,
  onClose,
}: Props) {
  const t = useProductsTFunction();

  const minDate = isBefore(itemToAssortmentTag.assortmentTag.validFrom, new Date())
    ? new Date()
    : itemToAssortmentTag.assortmentTag.validFrom;
  const maxDate = itemToAssortmentTag.assortmentTag.validTo ?? undefined;

  const { control, handleSubmit } = useForm<DateRangeSchema>({
    resolver: yupResolver(dateRangeSchema(t)),
    mode: 'onSubmit',
    defaultValues: {
      fromDate: minDate,
      toDate: maxDate,
    },
  });

  const fromDateController = useController({ name: 'fromDate', control });
  const toDateController = useController({ name: 'toDate', control });

  const onSubmit = useCallback(
    (dates: DateRangeSchema) => {
      addSupplierItemTag({
        supplierItemIds: [itemToAssortmentTag.mgSupplierItemId],
        validFrom: dates.fromDate.toISOString(),
        validTo: dates.toDate ? dates.toDate.toISOString() : undefined,
      });
      onClose();
    },
    [addSupplierItemTag, itemToAssortmentTag.mgSupplierItemId, onClose]
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {action === 'add'
          ? t('products.mgItem.supplierItem.addItemTo', {
              assortment: itemToAssortmentTag.assortmentTag.name,
            })
          : t('products.mgItem.supplierItem.editItem', {
              assortment: itemToAssortmentTag.assortmentTag.name,
            })}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TagDateRangeInfo assortmentTag={itemToAssortmentTag.assortmentTag} />
        <InputDatePicker
          {...fromDateController.field}
          required
          error={fromDateController.fieldState.error?.message}
          label={t('products.mgItem.addItemsToAssortment.fromDateType.label.singular')}
          minDate={minDate}
          maxDate={maxDate}
          margin="none"
        />
        <InputDatePicker
          {...toDateController.field}
          error={toDateController.fieldState.error?.message}
          label={t('products.mgItem.addItemsToAssortment.toDateType.label')}
          slotProps={{ field: { clearable: true } }}
          minDate={fromDateController.field.value ?? minDate}
          maxDate={maxDate}
          margin="none"
        />
      </DialogContent>
      <DialogActions>
        <OutlinedButton color="secondary" size="small" onClick={onClose}>
          {t('products.cancel')}
        </OutlinedButton>
        <ContainedButton onClick={handleSubmit(onSubmit)} color="secondary" size="small">
          {t('products.mgItem.addItemsToAssortment.addToAssortment')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
